import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

// Helper function to get content type
const getContentType = (content) => {
  if (content.title?.includes('Manuscript') || content.category) return 'manuscript';
  if (content.title?.includes('Publishing') || content.recommendedPath) return 'publishing';
  return 'action-plan';
};

// Helper function to create text content for exports
const createTextContent = (content, type) => {
  let text = '';
  
  if (type === 'manuscript') {
    text = `
MANUSCRIPT READINESS SCORECARD RESULTS
==========================================

Score: ${content.score}/${content.maxScore} (${content.percentage}%)
Category: ${content.category}
Date: ${content.date}

${content.description}

TOP STRENGTHS:
${content.strengths.map(s => `• ${s.text} (Score: ${s.score}/5)`).join('\n')}

IMPROVEMENT AREAS:
${content.weaknesses.map(w => `• ${w.text} (Score: ${w.score}/5)`).join('\n')}

${content.riskFlags.length > 0 ? `
POTENTIAL RISK FLAGS:
${content.riskFlags.map(flag => `• ${flag.text} score is below 3, suggesting a need for focused attention.`).join('\n')}
` : ''}

SCRIPTURE ENCOURAGEMENT:
"${content.scripture.verse}" — ${content.scripture.reference}
    `;
  } else if (type === 'publishing') {
    text = `
PUBLISHING PATH RECOMMENDATION
================================

Recommended Path: ${content.recommendedPath}
Confidence: ${content.confidence}
Date: ${content.date}

${content.description}

SCORE BREAKDOWN:
Traditional: ${content.scores.trad}
Hybrid: ${content.scores.hybrid}
Indie: ${content.scores.indie}

WHY THIS FITS YOU:
${content.whyFits.map((point) => `• ${point}`).join('\n')}

YOUR FIRST 3 FAITHFUL STEPS (7-14 days):
${content.steps.map((step, index) => `${index + 1}. ${step}`).join('\n')}

SCRIPTURE ENCOURAGEMENT:
"${content.scripture.verse}" — ${content.scripture.reference}

${content.riskFlags.length > 0 ? `
RISK & READINESS NOTES:
${content.riskFlags.map(flag => `- ${flag.title}: ${flag.message} (Suggestion: ${flag.suggestion})`).join('\n')}
` : ''}
    `;
  } else if (type === 'action-plan') {
    text = `
7-DAY FAITHFUL ACTION PLAN
============================

Track: ${content.track}
${content.subTrack ? `Sub-Track: ${content.subTrack}` : ''}
Time Block: ${content.timeBlock} minutes daily
Primary Outcome: ${content.primaryOutcome}
${content.secondaryOutcome && content.secondaryOutcome !== 'none' ? `Secondary Outcome: ${content.secondaryOutcome}` : ''}
Date: ${content.date}

YOUR 7-DAY PLAN:
${content.days.map(day => `
--------------------
Day ${day.day} ${day.isGraceDay ? '(Grace Day)' : ''} ${day.energyNote ? `(${day.energyNote})` : ''}
- Warm-up: ${day.warmUp}
- Core Task: ${day.coreTask.task} (${day.coreTask.minutes} minutes)
- Wrap-up: ${day.wrapUp}
- Accountability: ${day.accountability}
- Faith Focus: ${day.faithElement}
`).join('\n')}

--------------------
WEEKLY SUMMARY:
- 3 Key Milestones:
  • ${content.days[0].coreTask.task}
  • ${content.days[3].coreTask.task}
  • ${content.days[6].coreTask.task}

- Fallback Task: Light review and reflection (15 minutes)

This plan is designed to be flexible and grace-filled. Adjust as needed while maintaining forward momentum.
    `;
  }
  
  return text.trim();
};

// Helper function to create Word document content
const createWordContent = (content, type) => {
  let children = [];
  
  if (type === 'manuscript') {
    children = [
      new Paragraph({
        text: "Manuscript Readiness Scorecard Results",
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Score: ", bold: true }),
          new TextRun({ text: `${content.score}/${content.maxScore} (${content.percentage}%)` })
        ],
        spacing: { after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Category: ", bold: true }),
          new TextRun({ text: content.category })
        ],
        spacing: { after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Date: ", bold: true }),
          new TextRun({ text: content.date })
        ],
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: content.description,
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: "Top Strengths:",
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 200 }
      }),
      ...content.strengths.map(s => 
        new Paragraph({
          text: `${s.text} (Score: ${s.score}/5)`,
          bullet: { level: 0 }
        })
      ),
      new Paragraph({
        text: "Improvement Areas:",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      ...content.weaknesses.map(w => 
        new Paragraph({
          text: `${w.text} (Score: ${w.score}/5)`,
          bullet: { level: 0 }
        })
      ),
      ...(content.riskFlags.length > 0 ? [
        new Paragraph({
          text: "Potential Risk Flags:",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        ...content.riskFlags.map(flag => 
          new Paragraph({
            text: `${flag.text} score is below 3, suggesting a need for focused attention.`,
            bullet: { level: 0 }
          })
        ),
      ] : []),
      new Paragraph({
        text: "Scripture Encouragement:",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      new Paragraph({
        text: `"${content.scripture.verse}"`,
        italics: true,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: `— ${content.scripture.reference}`,
        alignment: AlignmentType.RIGHT
      })
    ];
  } else if (type === 'publishing') {
    children = [
      new Paragraph({
        text: "Publishing Path Recommendation",
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Recommended Path: ", bold: true }),
          new TextRun({ text: content.recommendedPath })
        ],
        spacing: { after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Confidence: ", bold: true }),
          new TextRun({ text: content.confidence })
        ],
        spacing: { after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Date: ", bold: true }),
          new TextRun({ text: content.date })
        ],
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: content.description,
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: "Score Breakdown:",
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 200 }
      }),
      new Paragraph({
        text: `Traditional: ${content.scores.trad}`,
        bullet: { level: 0 }
      }),
      new Paragraph({
        text: `Hybrid: ${content.scores.hybrid}`,
        bullet: { level: 0 }
      }),
      new Paragraph({
        text: `Indie: ${content.scores.indie}`,
        bullet: { level: 0 }
      }),
      new Paragraph({
        text: "Why This Fits You:",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      ...content.whyFits.map(point => 
        new Paragraph({
          text: point,
          bullet: { level: 0 }
        })
      ),
      new Paragraph({
        text: "Your First 3 Faithful Steps (7-14 days):",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      ...content.steps.map(step => 
        new Paragraph({
          text: step,
          bullet: { level: 0 }
        })
      ),
      ...(content.riskFlags.length > 0 ? [
        new Paragraph({
          text: "Risk & Readiness Notes:",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        ...content.riskFlags.map(flag => 
          new Paragraph({
            children: [
              new TextRun({ text: `${flag.title}: `, bold: true }),
              new TextRun({ text: `${flag.message} (Suggestion: ${flag.suggestion})` })
            ],
            bullet: { level: 0 }
          })
        )
      ] : []),
      new Paragraph({
        text: "Scripture Encouragement:",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      new Paragraph({
        text: `"${content.scripture.verse}"`,
        italics: true,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: `— ${content.scripture.reference}`,
        alignment: AlignmentType.RIGHT
      })
    ];
  } else if (type === 'action-plan') {
    children = [
      new Paragraph({
        text: "7-Day Faithful Action Plan",
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Track: ", bold: true }),
          new TextRun({ text: content.track })
        ],
        spacing: { after: 200 }
      }),
      ...(content.subTrack ? [
        new Paragraph({
          children: [
            new TextRun({ text: "Sub-Track: ", bold: true }),
            new TextRun({ text: content.subTrack })
          ],
          spacing: { after: 200 }
        })
      ] : []),
      new Paragraph({
        children: [
          new TextRun({ text: "Time Block: ", bold: true }),
          new TextRun({ text: `${content.timeBlock} minutes daily` })
        ],
        spacing: { after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Primary Outcome: ", bold: true }),
          new TextRun({ text: content.primaryOutcome })
        ],
        spacing: { after: 200 }
      }),
      ...(content.secondaryOutcome && content.secondaryOutcome !== 'none' ? [
        new Paragraph({
          children: [
            new TextRun({ text: "Secondary Outcome: ", bold: true }),
            new TextRun({ text: content.secondaryOutcome })
          ],
          spacing: { after: 200 }
        })
      ] : []),
      new Paragraph({
        children: [
          new TextRun({ text: "Date: ", bold: true }),
          new TextRun({ text: content.date })
        ],
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: "Your 7-Day Plan:",
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 200 }
      }),
      ...content.days.map(day => [
        new Paragraph({
          text: `Day ${day.day}${day.isGraceDay ? ' (Grace Day)' : ''}`,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 300, after: 200 }
        }),
        ...(day.energyNote ? [
          new Paragraph({
            text: day.energyNote,
            italics: true,
            spacing: { after: 200 }
          })
        ] : []),
        new Paragraph({
          text: `Warm-up: ${day.warmUp}`,
          bullet: { level: 0 }
        }),
        new Paragraph({
          text: `Core Task: ${day.coreTask.task} (${day.coreTask.minutes} minutes)`,
          bullet: { level: 0 }
        }),
        new Paragraph({
          text: `Wrap-up: ${day.wrapUp}`,
          bullet: { level: 0 }
        }),
        new Paragraph({
          text: `Accountability: ${day.accountability}`,
          bullet: { level: 0 }
        }),
        new Paragraph({
          text: `Faith Focus: ${day.faithElement}`,
          bullet: { level: 0 }
        }),
      ]).flat(),
      new Paragraph({
        text: "Weekly Summary:",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 }
      }),
      new Paragraph({
        text: "3 Key Milestones:",
        bold: true
      }),
      new Paragraph({
        text: content.days[0].coreTask.task,
        bullet: { level: 0 }
      }),
      new Paragraph({
        text: content.days[3].coreTask.task,
        bullet: { level: 0 }
      }),
      new Paragraph({
        text: content.days[6].coreTask.task,
        bullet: { level: 0 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Fallback Task: ", bold: true }),
          new TextRun({ text: "Light review and reflection (15 minutes)" })
        ],
        spacing: { before: 200 }
      }),
      new Paragraph({
        text: "This plan is designed to be flexible and grace-filled. Adjust as needed while maintaining forward momentum.",
        italics: true,
        spacing: { before: 400 }
      })
    ];
  }
  
  return children;
};

// Fallback download function
const fallbackDownload = (content, filename) => {
  const type = getContentType(content);
  const textContent = createTextContent(content, type);
  const blob = new Blob([textContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// PDF generation function
export const generatePDF = async (content, filename) => {
  try {
    const type = getContentType(content);
    const pdfContent = createTextContent(content, type);
    
    // Create PDF with better formatting
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set font
    pdf.setFont('helvetica');
    pdf.setFontSize(12);
    
    // Split text into lines that fit the page
    const lines = pdf.splitTextToSize(pdfContent, 170);
    let yPosition = 20;
    const lineHeight = 7;
    const pageHeight = pdf.internal.pageSize.height;
    const margin = 20;
    
    // Add content with proper page breaks
    lines.forEach((line, index) => {
      if (yPosition > pageHeight - margin) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, margin, yPosition);
      yPosition += lineHeight;
    });
    
    // Save the PDF
    pdf.save(filename);
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to text download
    fallbackDownload(content, filename.replace('.pdf', '.txt'));
    return false;
  }
};

// Word document generation function
export const generateWord = async (content, filename) => {
  try {
    const type = getContentType(content);
    const docChildren = createWordContent(content, type);
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: docChildren,
      }],
    });
    
    // Generate and download the document
    const buffer = await Packer.toBuffer(doc);
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    // Trigger download
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error generating Word document:', error);
    // Fallback to text download
    fallbackDownload(content, filename.replace('.docx', '.txt'));
    return false;
  }
};